import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  LayoutDashboard, Receipt, ShoppingBasket, Clock, Gauge, Download,
} from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { Card, CardContent } from '../components/ui/Card';
import FilterBar from '../components/analytics/FilterBar';
import { rangeFromPreset } from '../utils/dateUtils';

import analyticsService from '../services/analyticsService';
import basketService from '../services/basketService';
import brokerService from '../services/brokerService';
import { useActingAs } from '../context/ActingAsContext';
import { AnalyticsDataProvider } from '../context/AnalyticsDataContext';
import { isWhiteLabel } from '../config/tenantConfig';
import { cn } from '../utils/cn';

import type {
  AnalyticsFilters,
  AnalyticsSummary,
  CapitalHistoryResponse,
  DailyPnlPoint,
} from '../types/analytics';
import type { BrokerAccount } from '../types';

const SECTIONS: Array<{ path: string; label: string; icon: React.ReactNode; end?: boolean }> = [
  { path: '/others/analytics',             label: 'Overview',  icon: <LayoutDashboard className="w-4 h-4" />, end: true },
  { path: '/others/analytics/baskets',     label: 'Baskets',   icon: <ShoppingBasket className="w-4 h-4" /> },
  { path: '/others/analytics/trades',      label: 'Trades',    icon: <Receipt className="w-4 h-4" /> },
  { path: '/others/analytics/time',        label: 'Time',      icon: <Clock className="w-4 h-4" /> },
  { path: '/others/analytics/execution',   label: 'Execution', icon: <Gauge className="w-4 h-4" /> },
  { path: '/others/analytics/download',    label: 'Download',  icon: <Download className="w-4 h-4" /> },
];

const AnalyticsLayout: React.FC = () => {
  const [filters, setFilters] = useState<AnalyticsFilters>(() => ({
    // Default to the current Indian financial year (Apr 1 → today) rather
    // than a trailing 30-day window; FilterBar highlights "FY" as the active
    // selection on first load. Lazy initializer so rangeFromPreset runs once.
    ...rangeFromPreset('FY'),
    accounts: [],
    baskets: [],
    strategies: [],
  }));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [dailyPoints, setDailyPoints] = useState<DailyPnlPoint[]>([]);
  const [capital, setCapital] = useState<CapitalHistoryResponse | null>(null);

  const [accounts, setAccounts] = useState<Array<{ broker: string; clientId: string; label: string }>>([]);
  const [brokerAccounts, setBrokerAccounts] = useState<BrokerAccount[]>([]);
  const [baskets, setBaskets] = useState<Array<{ id: string; name: string }>>([]);
  const [strategies] = useState<Array<{ id: string; name: string }>>([]);

  const { actingAs } = useActingAs();

  const fetchBrokerAccounts = useCallback(async () => {
    try {
      const accs = await brokerService.getBrokerAccounts();
      setBrokerAccounts(accs);
      setAccounts(accs.map((a: any) => ({
        broker: a.broker_name,
        clientId: a.client_id,
        label: `${a.broker_name} · ${a.client_id}`,
      })));
    } catch { /* non-fatal */ }
  }, []);

  // Load filter options once.
  useEffect(() => {
    fetchBrokerAccounts();
    (async () => {
      try {
        const bks = await basketService.getBaskets();
        setBaskets(bks.map((b: any) => ({ id: b.basket_id, name: b.basket_name })));
      } catch { /* non-fatal */ }
    })();
  }, [fetchBrokerAccounts]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Layout fetches only the cross-section data (summary + daily + capital).
      // Each section pulls its own slice (top trades, breakdowns, positions, etc.).
      const [summaryR, dailyR, capR] = await Promise.all([
        analyticsService.getSummary(filters),
        analyticsService.getDailyPnl(filters),
        analyticsService.getCapitalHistory(filters),
      ]);
      setSummary(summaryR);
      setDailyPoints(dailyR.points || []);
      setCapital(capR);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load analytics.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, actingAs]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const refetchBrokerAccounts = useCallback(async () => {
    await fetchBrokerAccounts();
    // After capital edits, today's analytics-daily row gets patched by the
    // broker-account-manager side-effect. Re-fetch analytics so the new
    // capital denominator flows through summary / equity / capital-history.
    fetchAll();
  }, [fetchBrokerAccounts, fetchAll]);

  const value = useMemo(() => ({
    filters, setFilters,
    summary, capital, dailyPoints,
    accounts, brokerAccounts, baskets, strategies,
    loading, error,
    refetch: fetchAll,
    refetchBrokerAccounts,
  }), [filters, summary, capital, dailyPoints, accounts, brokerAccounts, baskets, strategies, loading, error, fetchAll, refetchBrokerAccounts]);

  const wl = isWhiteLabel();
  const activeClasses = wl
    ? 'bg-zebu-50 dark:bg-zebu-900/30 text-zebu-700 dark:text-zebu-300 border-l-2 border-zebu-600'
    : 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-l-2 border-blue-600';
  const inactiveClasses =
    'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border-l-2 border-transparent';

  // Rendered nested inside OthersLayout — the outer StandardLayout +
  // PageHeader come from there, so this layout owns only the analytics
  // filter bar, left rail, and content slot.
  return (
    <>
      {/* Sticky filter card */}
      <Card>
        <CardContent className="p-0">
          <FilterBar
            filters={filters}
            onChange={setFilters}
            onRefresh={fetchAll}
            loading={loading}
            accounts={accounts}
            baskets={baskets}
            strategies={strategies}
          />
        </CardContent>
      </Card>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Layout: sticky left rail (≥lg) + content. Below lg the rail becomes
          a horizontal scrolling tab strip above the content. */}
      <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-4">
        {/* Desktop rail */}
        <nav className="hidden lg:block">
          <div className="sticky top-20 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 py-2">
            {SECTIONS.map((s) => (
              <NavLink
                key={s.path}
                to={s.path}
                end={s.end}
                className={({ isActive }) => cn(
                  'flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors',
                  isActive ? activeClasses : inactiveClasses,
                )}
              >
                {s.icon}
                {s.label}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* Mobile tab strip — wraps onto multiple rows on narrow viewports
            instead of scrolling horizontally, so every section stays visible
            without swipe-to-discover (consistent with OthersLayout). */}
        <nav className="lg:hidden">
          <div className="flex flex-wrap items-center gap-1">
            {SECTIONS.map((s) => (
              <NavLink
                key={s.path}
                to={s.path}
                end={s.end}
                className={({ isActive }) => cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md',
                  isActive
                    ? (wl
                        ? 'bg-zebu-600 text-white'
                        : 'bg-blue-600 text-white')
                    : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300',
                )}
              >
                {s.icon}
                {s.label}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* Content area */}
        <main className="min-w-0 space-y-4">
          {loading && !summary ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner message="Loading analytics..." />
            </div>
          ) : (
            <AnalyticsDataProvider value={value}>
              <Outlet />
            </AnalyticsDataProvider>
          )}
        </main>
      </div>
    </>
  );
};

export default AnalyticsLayout;
